import { forwardRef, memo, useMemo, type ClipboardEvent, type FocusEvent, type FocusEventHandler, type InputHTMLAttributes } from 'react';
import type { NumberFormatValues } from 'react-number-format';
import { NumericFormat } from 'react-number-format';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { StyledInputContainer } from '../common/index.ts';
import { ALLOWED_DECIMAL_SEPARATORS, DEFAULT_MAX, DEFAULT_MIN, DEFAULT_TRANSLATIONS } from './constants.ts';
import { toPrecision } from './helpers.ts';
import { ReStyledInput as StyledInput } from './styled.ts';
import { type NumberInputTranslations, type NumberInputValue } from './types.ts';

export interface NumberInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'defaultValue' | 'onChange' | 'type' | 'onBlur' | 'step'> {
  value?: NumberInputValue;
  onChange?(value: NumberInputValue): void;
  min?: number;
  max?: number;
  minExclusive?: boolean;
  maxExclusive?: boolean;
  success?: boolean;
  warning?: boolean;
  error?: boolean;
  width?: number;
  dataTestId?: string;
  decimalPlaces?: number;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLButtonElement>;
  translations?: NumberInputTranslations;
}

export const NumberInput = memo(
  forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
    {
      value,
      onChange,
      min = DEFAULT_MIN,
      max = DEFAULT_MAX,
      minExclusive = false,
      maxExclusive = false,
      success,
      warning,
      error,
      width,
      dataTestId,
      decimalPlaces = 0,
      onBlur,
      disabled,
      translations,
      ...props
    },
    ref,
  ) {
    const mergedTranslations = useMemo<Required<NumberInputTranslations>>(
      () => ({
        ...DEFAULT_TRANSLATIONS,
        ...translations,
      }),
      [translations],
    );

    const placeholder = useMemo(() => {
      const gtSign = minExclusive ? '>' : '≥';
      const ltSign = maxExclusive ? '<' : '≤';
      return `${min} ${gtSign} ${mergedTranslations.value} ${ltSign} ${max}`;
    }, [max, maxExclusive, mergedTranslations.value, min, minExclusive]);

    const clampValue = useCallbackRef((newValue?: NumberInputValue) => {
      if (newValue === null || newValue === undefined) return null;

      if (minExclusive ? newValue <= min : newValue < min) {
        return minExclusive ? min + 10 ** -decimalPlaces : min;
      }
      if (maxExclusive ? newValue >= max : newValue > max) {
        return maxExclusive ? max - 10 ** -decimalPlaces : max;
      }
      return toPrecision(newValue, decimalPlaces);
    });

    const onBlurHandler = useCallbackRef((event: FocusEvent<HTMLInputElement>) => {
      onChange?.(clampValue(value));
      onBlur?.(event);
    });

    const handleChange = useCallbackRef((values: NumberFormatValues) => {
      onChange?.(values.floatValue ?? null);
    });

    const handlePaste = useCallbackRef((e: ClipboardEvent<HTMLInputElement>) => {
      const pastedText = e.clipboardData.getData('text');

      // Check if pasted text contains comma and no dot (comma as decimal separator)
      if (pastedText.includes(',') && !pastedText.includes('.')) {
        e.preventDefault();

        // Get the input element and current selection
        const input = e.target as HTMLInputElement;
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const currentValue = input.value || '';

        // Replace comma with dot in pasted text
        const normalizedPastedText = pastedText.replace(',', '.');

        // Construct new value by inserting normalized text at cursor position
        const newValue = currentValue.substring(0, start) + normalizedPastedText + currentValue.substring(end);

        // Parse and update the value
        const parsedValue = parseFloat(newValue);
        if (!Number.isNaN(parsedValue)) {
          onChange?.(parsedValue);
        }
      }

      // Call original onPaste if provided
      props.onPaste?.(e);
    });

    // This is a workaround to fix the issue with the input value not being cleared when the value is null or undefined.
    // https://github.com/s-yadav/react-number-format/issues/727
    const numericFormatValue = value ?? '';

    return (
      <StyledInputContainer
        width={width}
        $inputDisabled={props.readOnly || disabled}
        $success={success}
        $warning={warning}
        $error={error}
        data-testid={dataTestId}>
        <NumericFormat
          customInput={StyledInput}
          value={numericFormatValue}
          onValueChange={handleChange}
          onPaste={handlePaste}
          onBlur={onBlurHandler}
          disabled={disabled}
          {...props}
          placeholder={props.placeholder ?? placeholder}
          getInputRef={ref}
          allowNegative={min < 0}
          decimalScale={decimalPlaces}
          allowedDecimalSeparators={ALLOWED_DECIMAL_SEPARATORS}
        />
      </StyledInputContainer>
    );
  }),
);

export type { NumberInputValue, NumberInputTranslations };
